'use client'

import { ChevronRightIcon } from '@repo/ui/ChevronRightIcon'
import { Link, useLocation } from 'react-router-dom'
import { useState } from 'react'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { useLookupsStore } from '@repo/store/useLookupsStore'
import { useMenuItemsStore } from '@repo/store/useMenuItemsStore'
import { useTheme } from '@mui/material/styles'
import MenuItem from '@mui/material/MenuItem'
import MUIStack from '@mui/material/Stack'
import MUITab from '@mui/material/Tab'
import MUITabs from '@mui/material/Tabs'
import Popover from '@mui/material/Popover'
import styles from './MattersWorkspaceNavigation.module.scss'

const matterWorkspaceLinksArray = [
  {
    link: '/case-management/matters/matter-workspace',
    title: 'Dashboard',
    dropdown: false
  },
  {
    link: '/case-management/matters/matter-history',
    title: 'Matter History',
    dropdown: false
  },
  {
    link: '/case-management/matters/document-bundles',
    title: 'Document bundles',
    dropdown: false
  },
  {
    link: '/accounts/client-ledger',
    title: 'Financial',
    dropdown: false
  },
  {
    link: '/time-recording/time-ledger',
    title: 'Time',
    dropdown: false
  },
  {
    link: '/case-management/matters/key-dates',
    title: 'Key Dates',
    dropdown: false
  },
  {
    link: '/case-management/matters/workflows',
    title: 'Workflows',
    dropdown: false
  },
  {
    link: '/case-management/matters/contract-work-forms',
    title: 'Contract work forms',
    dropdown: false
  },
  {
    link: '/case-management/matters/custom-data-pages',
    title: 'Custom Data Pages'
  },
  {
    link: '/case-management/matters/custom-matter-views',
    title: 'Custom Matter Views'
  },
  {
    link: '/case-management/matters/custom-questionnaires',
    title: 'Custom Questionnaires'
  },
  {
    link: '/case-management/matters/third-party-forms',
    title: 'Third Party Forms'
  }
]

export const MattersWorkspaceNavigation = () => {
  const theme = useTheme()
  const { pageTitleBlockIsVisible, environmentName } = useGlobalSettingsStore()
  const { allowedMenuIds } = useMenuItemsStore()
  const { enableBundledocs } = useLookupsStore()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [flip, setFlip] = useState<boolean>(false)

  const useRouteMatch = (patterns: any) => {
    const location = useLocation()
    return patterns.find((pattern: any) => location.pathname.startsWith(pattern)) || null
  }

  const routeMatch = useRouteMatch(matterWorkspaceLinksArray.map(({ link }) => link))

  const currentTab = routeMatch ?? ''

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
    setFlip(true)
  }

  const handlePopoverClose = () => {
    setAnchorEl(null)
    setFlip(false)
  }

  const handleTabChange = (link: string) => {
    setAnchorEl(null)
    setFlip(false)
  }

  const open = Boolean(anchorEl)

  const moreMenuLinks = ['/case-management/matters/custom-data-pages', '/case-management/matters/custom-matter-views', '/case-management/matters/custom-questionnaires', '/case-management/matters/third-party-forms']

  return (
    <MUIStack
      flexDirection={'row'}
      justifyContent={'flex-start'}
      alignItems={'center'}
      gap={'3rem'}
      whiteSpace={'pre'}
      className={styles.navContainer}
      sx={{ marginTop: pageTitleBlockIsVisible ? 0 : '140px' }}
      data-testid="MattersWorkspaceNavigation"
    >
      <MUITabs
        value={currentTab || false}
        aria-label="MattersWorkspaceNavigation"
        className="workspace-details-tabs"
        sx={{
          borderBottom: 'none',
          mt: '0px',
          [`@media only screen and (max-width: 1200px)`]: {
            [`& .MuiTab-root`]: {
              maxWidth: '120px'
            }
          }
        }}
      >
        <MUITab
          label="Dashboard"
          role="tab"
          className={`workspace-details-tab ${allowedMenuIds?.includes(1200) ? '' : styles.disabled}`}
          value="/case-management/matters/matter-workspace"
          to="/case-management/matters/matter-workspace"
          component={Link}
          sx={{
            pointerEvents: allowedMenuIds?.includes(1200) ? 'auto' : 'none',
            opacity: allowedMenuIds?.includes(1200) ? 1 : 0.5
          }}
        />

        <MUITab
          label="Matter History"
          role="tab"
          className={`workspace-details-tabs ${allowedMenuIds?.includes(3990) ? '' : styles.disabled}`}
          value="/case-management/matters/matter-history"
          to="/case-management/matters/matter-history"
          component={Link}
          sx={{
            pointerEvents: allowedMenuIds?.includes(3990) ? 'auto' : 'none',
            opacity: allowedMenuIds?.includes(3990) ? 1 : 0.5
          }}
        />
        <MUITab
          label="Financial"
          role="tab"
          className={`workspace-details-tabs ${allowedMenuIds?.includes(5000) ? '' : styles.disabled}`}
          to="/accounts/client-ledger"
          value="/accounts/client-ledger"
          component={Link}
          sx={{
            pointerEvents: allowedMenuIds?.includes(5000) ? 'auto' : 'none',
            opacity: allowedMenuIds?.includes(5000) ? 1 : 0.5
          }}
        />
        <MUITab
          label="Time"
          role="tab"
          className={`workspace-details-tabs ${allowedMenuIds?.includes(2200) ? '' : styles.disabled}`}
          to="/time-recording/time-ledger"
          value="/time-recording/time-ledger"
          component={Link}
          sx={{
            pointerEvents: allowedMenuIds?.includes(2200) ? 'auto' : 'none',
            opacity: allowedMenuIds?.includes(2200) ? 1 : 0.5
          }}
        />
        <MUITab
          label="Key Dates"
          role="tab"
          className={`workspace-details-tabs ${allowedMenuIds?.includes(3100) ? '' : styles.disabled}`}
          to="/case-management/matters/key-dates"
          value="/case-management/matters/key-dates"
          component={Link}
          sx={{
            pointerEvents: allowedMenuIds?.includes(3100) ? 'auto' : 'none',
            opacity: allowedMenuIds?.includes(3100) ? 1 : 0.5
          }}
        />
        <MUITab
          label="Workflows"
          role="tab"
          className={`workspace-details-tabs ${allowedMenuIds?.includes(3600) ? '' : styles.disabled}`}
          to="/case-management/matters/workflows"
          value="/case-management/matters/workflows"
          component={Link}
          sx={{
            pointerEvents: allowedMenuIds?.includes(3600) ? 'auto' : 'none',
            opacity: allowedMenuIds?.includes(3600) ? 1 : 0.5
          }}
        />
        <MUITab
          label="Contract work forms"
          role="tab"
          className={`workspace-details-tabs ${allowedMenuIds?.includes(3800) ? '' : styles.disabled}`}
          to="/case-management/matters/contract-work-forms"
          value="/case-management/matters/contract-work-forms"
          component={Link}
          sx={{
            pointerEvents: allowedMenuIds?.includes(3800) ? 'auto' : 'none',
            opacity: allowedMenuIds?.includes(3800) ? 1 : 0.5
          }}
        />
        <MUITab
          label="More"
          role="tab"
          className={`workspace-details-tabs ${allowedMenuIds?.some((id: number) => [3200, 1500, 1600].includes(id)) ? '' : styles.disabled}`}
          value={ moreMenuLinks.includes(currentTab) ? currentTab : '' }
          onClick={handlePopoverOpen}
          aria-haspopup="true"
          aria-controls="customDataPopover"
          sx={{
            pointerEvents: allowedMenuIds?.some((id: number) => [3200, 1500, 1600].includes(id)) ? 'auto' : 'none',
            opacity: allowedMenuIds?.some((id: number) => [3200, 1500, 1600].includes(id)) ? 1 : 0.5
          }}
        />
        <ChevronRightIcon
          className={`${styles.navChevron} ${flip ? styles.flip : ''}`}
          onClick={handlePopoverOpen}
          sx={{
            color: theme.palette.text.secondary
          }}
        />
      </MUITabs>

      <Popover
        id="customDataPopover"
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
        slotProps={{ paper: { sx: { overflow: 'visible', backgroundColor: '#FFF' } } }}
      >
        <div className={styles.popoverArrow}></div>
        <div style={{ padding: '6px' }}>
          {enableBundledocs && (
            <MenuItem
              className={styles.popoverItem}
              onClick={() => handleTabChange('/case-management/matters/document-bundles')}
              component={Link}
              to="/case-management/matters/document-bundles"
              sx={{
                pointerEvents: allowedMenuIds?.some((id: number) => [1200].includes(id)) ? 'auto' : 'none',
                opacity: allowedMenuIds?.some((id: number) => [1200].includes(id)) ? 1 : 0.5
              }}
            >
              Document bundles
            </MenuItem>
          )}
          <MenuItem
            className={styles.popoverItem}
            onClick={() => handleTabChange('/case-management/matters/custom-data-pages')}
            component={Link}
            to="/case-management/matters/custom-data-pages"
            sx={{
              pointerEvents: allowedMenuIds?.some((id: number) => [3200].includes(id)) ? 'auto' : 'none',
              opacity: allowedMenuIds?.some((id: number) => [3200].includes(id)) ? 1 : 0.5
            }}
          >
            Custom data pages
          </MenuItem>
          <MenuItem
            className={styles.popoverItem}
            onClick={() => handleTabChange('/case-management/matters/custom-matter-views')}
            component={Link}
            to="/case-management/matters/custom-matter-views"
            sx={{
              pointerEvents: allowedMenuIds?.some((id: number) => [1500].includes(id)) ? 'auto' : 'none',
              opacity: allowedMenuIds?.some((id: number) => [1500].includes(id)) ? 1 : 0.5
            }}
          >
            Custom matter views
          </MenuItem>
          <MenuItem
            className={styles.popoverItem}
            onClick={() => handleTabChange('/case-management/matters/custom-questionnaires')}
            component={Link}
            to="/case-management/matters/custom-questionnaires"
            sx={{
              pointerEvents: allowedMenuIds?.some((id: number) => [1600].includes(id)) ? 'auto' : 'none',
              opacity: allowedMenuIds?.some((id: number) => [1600].includes(id)) ? 1 : 0.5
            }}
          >
            Custom questionnaires
          </MenuItem>

          <MenuItem
            className={styles.popoverItem}
            onClick={() => handleTabChange('/case-management/matters/third-party-forms')}
            component={Link}
            to="/case-management/matters/third-party-forms"
            sx={{
              pointerEvents: allowedMenuIds?.some((id: number) => [1600].includes(id)) ? 'auto' : 'none',
              opacity: allowedMenuIds?.some((id: number) => [1600].includes(id)) ? 1 : 0.5
            }}
          >
            Third party forms
          </MenuItem>
        </div>
      </Popover>
    </MUIStack>
  )
}